/**
 * Entry point — desktop build (Electron + SQLite).
 *
 * The legacy browser sync engines (syncEngine, genericSyncEngine, autoCacheEngine,
 * deltaPullEngine, offlineDataBridge) have been removed from this project as part
 * of the desktop migration.  All persistence goes through SQLite via window.pms IPC.
 *
 * This file is intentionally identical in behaviour to main-desktop.jsx so that
 * Vite's desktop build (VITE_BUILD_TARGET=desktop, entry = index-desktop.html)
 * and any tooling that still points at main.jsx both produce the correct output.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import './index.css';
import { initDesktopBridge } from './platform/desktopBridge.js';

// ── Purge any stale IndexedDB / service-worker state from old browser sessions ─
async function purgeStaleWebStorage() {
  if ('indexedDB' in window) {
    try {
      if (typeof indexedDB.databases === 'function') {
        const dbs = await indexedDB.databases();
        await Promise.all(
          dbs.map(({ name }) => {
            if (!name) return Promise.resolve();
            return new Promise((resolve) => {
              const req = indexedDB.deleteDatabase(name);
              req.onsuccess = resolve;
              req.onerror   = resolve;
              req.onblocked = resolve;
            });
          })
        );
      } else {
        const known = ['hotelmis-reservation','hotelmis-genericSync','hotelmis-offlineQueue','hotelmis-deltaSync'];
        await Promise.all(known.map(name => new Promise((resolve) => {
          const req = indexedDB.deleteDatabase(name);
          req.onsuccess = resolve;
          req.onerror   = resolve;
          req.onblocked = resolve;
        })));
      }
    } catch (e) {
      console.warn('[desktop] IndexedDB purge failed (non-fatal):', e);
    }
  }

  if ('caches' in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    } catch (_) {}
  }

  if ('serviceWorker' in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    } catch (_) {}
  }
}

purgeStaleWebStorage().finally(() => {
  initDesktopBridge();
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
});
